import { CaretSortIcon, MagnifyingGlassIcon } from "@radix-ui/react-icons";
import {
  ColumnDef,
  SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import clsx from "clsx";
import dayjs from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  CompanyUser,
  useGetUsersForCompany,
} from "src/api/Companies/companies";
import {
  CustomerData,
  useGetCustomersWithDebouncing,
} from "src/api/General/customers";
import {
  CourseEnrollmentData,
  LessonData,
  ReducedCourseData,
  useCreateCourse,
  useCreateLesson,
  useEnrollUserInCourse,
  useGetAllEnrollments,
  useGetCourses,
  useGetLessons,
} from "src/api/lms/lms";
import InputHeading from "src/components/ui/Headings/InputHeading";
import { Button } from "src/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "src/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "src/components/ui/dialog";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "src/components/ui/hover-card";
import { Loading } from "src/components/ui/loading";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "src/components/ui/popover";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "src/components/ui/tabs";
import { TextInput } from "src/components/ui/text-input";
import useDebounce from "src/utils/customHooks/useDebounce";
import { fuzzyFilter } from "../Services/Projects/ProjectsTable";

type Props = {};

export default function LMS({}: Props) {
  const navigate = useNavigate();
  const { phaseId, tab } = useParams();
  if (!tab) {
    navigate(`/lms/courses`, {
      replace: true,
    });
  } else {
    if (tab !== "courses" && tab !== "lessons" && tab !== "enrollments") {
      navigate(`/lms/courses`, {
        replace: true,
      });
    }
  }
  return (
    <div className="flex h-full flex-col overflow-y-auto">
      <div className="flex h-12 shrink-0 items-center border-b border-gray-200/70 px-8">
        <div className="flex select-none items-center space-x-2 text-sm">
          LMS
        </div>
      </div>
      <div className="pt-2">
        <Tabs
          defaultValue="courses"
          value={tab}
          className="flex flex-1 flex-col overflow-auto pt-1"
          onValueChange={(value) => {
            navigate(`/lms/${value}`);
          }}
        >
          <TabsList className="shrink-0 border-gray-200/70 px-8">
            <TabsTrigger className="font-normal text-gray-500" value="courses">
              Courses
            </TabsTrigger>
            <TabsTrigger className="font-normal text-gray-500" value="lessons">
              Lessons
            </TabsTrigger>
            <TabsTrigger
              className="font-normal text-gray-500"
              value="enrollments"
            >
              Enrollments
            </TabsTrigger>
          </TabsList>
          <div className="flex-1 overflow-auto">
            <TabsContent value="courses" className="h-full">
              <Courses />
            </TabsContent>
            <TabsContent value="lessons" className="h-full overflow-auto">
              <Lessons />
            </TabsContent>
            <TabsContent value="enrollments" className="h-full overflow-auto">
              <Enrollments />
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </div>
  );
}

function Courses() {
  const { data: courses } = useGetCourses();
  const [search, setSearch] = useState("");
  return (
    <>
      <div className="flex h-16 items-center justify-between border-b border-gray-200/70 px-8">
        <div className="flex items-center space-x-2">
          <div className="relative">
            <TextInput
              className="h-8 w-72 pl-8"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search.."
              autoFocus
            />
            <div className="absolute inset-y-0 left-0 flex items-center pl-2.5">
              <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
            </div>
          </div>
        </div>

        <NewCourseButton />
      </div>
      <div className="flex flex-1 flex-col overflow-y-auto">
        {!!courses ? (
          <>
            {!!courses.length ? (
              <>
                <CoursesTable
                  courses={courses}
                  search={search}
                  setSearch={setSearch}
                />
              </>
            ) : (
              <div className="mx-auto mt-[10vh]">
                <div className="flex">
                  <div className="h-20 w-20"></div>
                  <div className="h-20 w-[420px] bg-gradient-to-t from-gray-200 to-transparent px-[1px]">
                    <div className="h-full w-full bg-white" />
                  </div>
                  <div className="h-20 w-20"></div>
                </div>
                <div className="flex">
                  <div className="w-20 bg-gradient-to-l from-gray-200 to-white py-[1px]">
                    <div className="h-full w-full bg-white" />
                  </div>
                  <div className="w-[420px] border bg-gray-50/70 p-12">
                    <div className="text-xl font-medium">Courses</div>
                    <div className="pt-2.5 font-light text-gray-700">
                      All courses will show up here.
                    </div>
                    <div className="pt-6">
                      <NewCourseButton />
                    </div>
                  </div>
                  <div className="w-20 bg-gradient-to-r from-gray-200 to-white py-[1px]">
                    <div className="h-full w-full bg-white" />
                  </div>
                </div>
                <div className="flex">
                  <div className="h-20 w-20"></div>
                  <div className="h-20 w-[420px] bg-gradient-to-b from-gray-200 to-white px-[1px]">
                    <div className="h-full w-full bg-white" />
                  </div>
                  <div className="h-20 w-20"></div>
                </div>
              </div>
            )}
          </>
        ) : (
          <Loading className="mt-[20vh]" />
        )}
      </div>
    </>
  );
}

function NewCourseButton() {
  const createNewCourseMutation = useCreateCourse();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant={"secondary"}>New Course</Button>
      </DialogTrigger>
      <DialogContent className="max-w-xl">
        <DialogHeader className="">
          <DialogTitle>New Course</DialogTitle>
          <DialogDescription>
            Create a course to give a structure to your lessons.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          <div className="space-y-1">
            <InputHeading heading="Name" />
            <TextInput
              className="w-72"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <InputHeading heading="Description" />
            <textarea
              className="block w-[400px] resize-none rounded-md border-input p-2.5 py-2 text-sm shadow-sm outline-none transition-all placeholder:text-gray-300 focus:border-primary focus:outline-none focus:ring-[0.5px] focus:ring-primary"
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
              }}
              ref={textAreaRef}
              rows={3}
              placeholder="Description..."
            />
          </div>
        </div>
        <div className="flex justify-end space-x-2 pt-4">
          <Button variant={"secondary"}>Cancel</Button>
          <Button
            onClick={() => {
              createNewCourseMutation.mutate({
                name,
                description,
                lessonIds: [],
              });
            }}
          >
            {createNewCourseMutation.isLoading ? "Creating..." : "Create"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CoursesTable({
  courses,
  search,
  setSearch,
}: {
  courses: ReducedCourseData[];
  search: string;
  setSearch: (search: string) => void;
}) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const columns: ColumnDef<ReducedCourseData>[] = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        cell: ({ row }) => {
          return <div>{row.original.name}</div>;
        },
      },
      {
        accessorKey: "activeEnrollments",
        header: "Active Enrollments",
        cell: ({ row }) => {
          return <div className="w-40">{row.original.noOfEnrollments}</div>;
        },
      },
      {
        accessorKey: "numberOfLessons",
        header: "Lessons",
        cell: ({ row }) => {
          return <div className="w-40">{row.original.lessonIds.length}</div>;
        },
      },
    ],
    []
  );
  const table = useReactTable<ReducedCourseData>({
    data: courses ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    state: {
      sorting,
      globalFilter: search,
    },
    globalFilterFn: fuzzyFilter,
    onSortingChange: setSorting,
    onGlobalFilterChange: setSearch,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });
  const navigate = useNavigate();
  return (
    <div className="flex-1 overflow-auto">
      <div className="max-h-full max-w-full overflow-auto">
        <table
          style={{ borderCollapse: "separate", borderSpacing: 0 }}
          className="w-full"
        >
          <thead className="overflow-auto">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="border-b">
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    scope="col"
                    className={
                      "sticky top-0 z-10 h-11 whitespace-nowrap border-b border-r border-gray-200/70 bg-gray-50 px-6 text-left text-xs font-normal uppercase tracking-wide text-gray-400 first:left-0 first:z-20 first:w-full first:pl-8"
                    }
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            <>
              {table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className="group relative rounded-lg hover:cursor-pointer hover:bg-gray-50"
                  onClick={() => {
                    navigate(`/lms/courses/${row.original._id}`);
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className={
                        "whitespace-nowrap border-r border-b border-gray-100 px-6 py-3 text-sm text-gray-500 first:sticky first:left-0 first:z-10 first:bg-white first:pr-6 first:pl-8 first:font-medium first:text-gray-900 first:group-hover:bg-gray-50"
                      }
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Lessons() {
  const { data: lessons } = useGetLessons();
  const [search, setSearch] = useState("");
  return (
    <>
      <div className="flex h-16 items-center justify-between border-b border-gray-200/70 px-8">
        <div className="flex items-center space-x-2">
          <div className="relative">
            <TextInput
              className="h-8 w-72 pl-8"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search.."
              autoFocus
            />
            <div className="absolute inset-y-0 left-0 flex items-center pl-2.5">
              <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
            </div>
          </div>
        </div>

        <NewLessonButton />
      </div>
      <div className="flex flex-1 flex-col overflow-y-auto">
        {!!lessons ? (
          <>
            {!!lessons.length ? (
              <>
                <LessonsTable
                  lessons={lessons}
                  search={search}
                  setSearch={setSearch}
                />
              </>
            ) : (
              <div className="mx-auto mt-[10vh]">
                <div className="flex">
                  <div className="h-20 w-20"></div>
                  <div className="h-20 w-[420px] bg-gradient-to-t from-gray-200 to-transparent px-[1px]">
                    <div className="h-full w-full bg-white" />
                  </div>
                  <div className="h-20 w-20"></div>
                </div>
                <div className="flex">
                  <div className="w-20 bg-gradient-to-l from-gray-200 to-white py-[1px]">
                    <div className="h-full w-full bg-white" />
                  </div>
                  <div className="w-[420px] border bg-gray-50/70 p-12">
                    <div className="text-xl font-medium">Lessons</div>
                    <div className="pt-2.5 font-light text-gray-700">
                      All lessons will show up here.
                    </div>
                    <div className="pt-6">
                      <NewLessonButton />
                    </div>
                  </div>
                  <div className="w-20 bg-gradient-to-r from-gray-200 to-white py-[1px]">
                    <div className="h-full w-full bg-white" />
                  </div>
                </div>
                <div className="flex">
                  <div className="h-20 w-20"></div>
                  <div className="h-20 w-[420px] bg-gradient-to-b from-gray-200 to-white px-[1px]">
                    <div className="h-full w-full bg-white" />
                  </div>
                  <div className="h-20 w-20"></div>
                </div>
              </div>
            )}
          </>
        ) : (
          <Loading className="mt-[20vh]" />
        )}
      </div>
    </>
  );
}

function NewLessonButton() {
  const createNewLessonMutation = useCreateLesson();
  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant={"secondary"}>New Lesson</Button>
      </DialogTrigger>
      <DialogContent className="max-w-xl">
        <DialogHeader className="">
          <DialogTitle>New Lesson</DialogTitle>
          <DialogDescription>
            Lessons are the building blocks for courses and cover a specific
            topic.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          <div className="space-y-1">
            <InputHeading heading="Title" />
            <TextInput
              className="w-72"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              autoFocus
            />
          </div>
          <div className="space-y-1">
            <InputHeading heading="Video URL" />
            <TextInput
              className="w-72"
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              placeholder="https://www.youtube.com/watch?v=dQw4w9WgXcQ"
            />
          </div>
          {/* <div className="space-y-1">
            <InputHeading heading="Summary" />
            <textarea
              className="block w-[400px] resize-none rounded-md border-input p-2.5 py-2 text-sm shadow-sm outline-none transition-all placeholder:text-gray-300 focus:border-primary focus:outline-none focus:ring-[0.5px] focus:ring-primary"
              value={summary}
              onChange={(e) => {
                setSummary(e.target.value);
              }}
              ref={textAreaRef}
              rows={3}
              placeholder="What will this lesson cover?"
            />
          </div> */}
        </div>
        <div className="flex justify-end space-x-2 pt-4">
          <Button variant={"secondary"}>Cancel</Button>
          <Button
            onClick={() => {
              createNewLessonMutation.mutate({
                title,
                summary: "",
                videoUrl,
                description: "",
              });
            }}
          >
            {createNewLessonMutation.isLoading ? "Creating..." : "Create"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LessonsTable({
  lessons,
  search,
  setSearch,
}: {
  lessons: LessonData[];
  search: string;
  setSearch: (search: string) => void;
}) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const columns: ColumnDef<LessonData>[] = useMemo(
    () => [
      {
        accessorKey: "title",
        header: "Title",
        cell: ({ row }) => {
          return <div>{row.original.title}</div>;
        },
      },
      {
        accessorKey: "createdBy",
        header: "Created By",
        cell: ({ row }) => {
          return <div>{row.original.createdBy.name}</div>;
        },
      },
      {
        accessorKey: "createdAt",
        header: "Created At",
        cell: ({ row }) => {
          return (
            <div>{dayjs(row.original.createdAt).format("MMM DD, YYYY")}</div>
          );
        },
      },
    ],
    []
  );
  const table = useReactTable<LessonData>({
    data: lessons ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    state: {
      sorting,
      globalFilter: search,
    },
    globalFilterFn: fuzzyFilter,
    onSortingChange: setSorting,
    onGlobalFilterChange: setSearch,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });
  const navigate = useNavigate();
  return (
    <div className="flex-1 overflow-auto">
      <div className="max-h-full max-w-full overflow-auto">
        <table
          style={{ borderCollapse: "separate", borderSpacing: 0 }}
          className="w-full"
        >
          <thead className="overflow-auto">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="border-b">
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    scope="col"
                    className={
                      "sticky top-0 z-10 h-11 whitespace-nowrap border-b border-r border-gray-200/70 bg-gray-50 px-6 text-left text-xs font-normal uppercase tracking-wide text-gray-400 first:left-0 first:z-20 first:w-full first:pl-8"
                    }
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            <>
              {table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className="group relative rounded-lg hover:cursor-pointer hover:bg-gray-50"
                  onClick={() => {
                    navigate(`/lms/lessons/${row.original._id}`);
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className={
                        "whitespace-nowrap border-r border-b border-gray-100 px-6 py-3 text-sm text-gray-500 first:sticky first:left-0 first:z-10 first:bg-white first:pr-6 first:pl-8 first:font-medium first:text-gray-900 first:group-hover:bg-gray-50"
                      }
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Enrollments() {
  const { data: enrollments } = useGetAllEnrollments();
  const [search, setSearch] = useState("");
  return (
    <>
      <div className="flex h-16 items-center justify-between border-b border-gray-200/70 px-8">
        <div className="flex items-center space-x-2">
          <div className="relative">
            <TextInput
              className="h-8 w-72 pl-8"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search.."
              autoFocus
            />
            <div className="absolute inset-y-0 left-0 flex items-center pl-2.5">
              <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
            </div>
          </div>
        </div>

        <NewEnrollmentButton />
      </div>
      <div className="flex flex-1 flex-col overflow-y-auto">
        {!!enrollments ? (
          <>
            {!!enrollments.length ? (
              <>
                <EnrollmentsTable
                  enrollments={enrollments}
                  search={search}
                  setSearch={setSearch}
                />
              </>
            ) : (
              <div className="mx-auto mt-[10vh]">
                <div className="flex">
                  <div className="h-20 w-20"></div>
                  <div className="h-20 w-[420px] bg-gradient-to-t from-gray-200 to-transparent px-[1px]">
                    <div className="h-full w-full bg-white" />
                  </div>
                  <div className="h-20 w-20"></div>
                </div>
                <div className="flex">
                  <div className="w-20 bg-gradient-to-l from-gray-200 to-white py-[1px]">
                    <div className="h-full w-full bg-white" />
                  </div>
                  <div className="w-[420px] border bg-gray-50/70 p-12">
                    <div className="text-xl font-medium">Enrollments</div>
                    <div className="pt-2.5 font-light text-gray-700">
                      All enrollments will show up here.
                    </div>
                    <div className="pt-6">
                      <NewEnrollmentButton />
                    </div>
                  </div>
                  <div className="w-20 bg-gradient-to-r from-gray-200 to-white py-[1px]">
                    <div className="h-full w-full bg-white" />
                  </div>
                </div>
                <div className="flex">
                  <div className="h-20 w-20"></div>
                  <div className="h-20 w-[420px] bg-gradient-to-b from-gray-200 to-white px-[1px]">
                    <div className="h-full w-full bg-white" />
                  </div>
                  <div className="h-20 w-20"></div>
                </div>
              </div>
            )}
          </>
        ) : (
          <Loading className="mt-[20vh]" />
        )}
      </div>
    </>
  );
}

function CustomerCombobox({
  customer,
  setCustomer,
}: {
  customer: CustomerData | null;
  setCustomer: (customer: CustomerData | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [customerSearch, setCustomerSearch] = useState("");
  const [options, setOptions] = useState<CustomerData[]>([]);
  const { debouncedValue: debouncedSearch, isTyping } = useDebounce(
    customerSearch,
    500
  );
  const { paginatedCustomers, isError, isFetching, isLoading, isSuccess } =
    useGetCustomersWithDebouncing({
      search: debouncedSearch,
    });

  useEffect(() => {
    if (paginatedCustomers) {
      setOptions(paginatedCustomers.customers);
    }
  }, [paginatedCustomers]);
  return (
    <Popover modal={true} open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={"secondary"}
          className="h-fit min-h-[36px] w-72 justify-between gap-1 pl-2.5"
        >
          {!!customer ? (
            <div className="truncate">{customer.name}</div>
          ) : (
            <div className="text-sm font-normal text-gray-300">
              Select Company
            </div>
          )}
          <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="pointer-events-auto w-60 p-0" align="start">
        <Command className="relative">
          <CommandInput
            placeholder="Search company..."
            className="h-9"
            value={customerSearch}
            onValueChange={(s) => {
              setCustomerSearch(s);
            }}
          />
          {(isFetching || isTyping) && (
            <div className="absolute top-0 right-0 flex items-center pr-2.5 pt-2.5">
              <Loading className="h-4 w-4 border-[3px]" />
            </div>
          )}
          {!isFetching && !isTyping && (
            <CommandEmpty>No company found.</CommandEmpty>
          )}
          <CommandGroup>
            {options.map((company) => (
              <CommandItem
                key={company._id.toString()}
                value={company.name}
                onSelect={() => {
                  setCustomer(company);
                  setOpen(false);
                }}
                className="gap-2"
              >
                <div className="flex items-center space-x-2">
                  <span className="truncate">{company.name}</span>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function NewEnrollmentButton() {
  const createNewEnrollmentMutation = useEnrollUserInCourse();
  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [customer, setCustomer] = useState<CustomerData | null>(null);
  const [course, setCourse] = useState<ReducedCourseData | null>(null);
  const [user, setUser] = useState<CompanyUser | null>(null);
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant={"secondary"}>Enroll User</Button>
      </DialogTrigger>
      <DialogContent className="max-w-xl">
        <DialogHeader className="">
          <DialogTitle>New Enrollment</DialogTitle>
          <DialogDescription>Enroll a user in a course.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          <div className="space-y-1">
            <InputHeading heading="Course" />
            <CourseCombobox course={course} setCourse={setCourse} />
          </div>
          <div className="space-y-1">
            <InputHeading heading="Company" />
            <CustomerCombobox customer={customer} setCustomer={setCustomer} />
          </div>
          <div className="space-y-1">
            <InputHeading heading="User" />
            {!!customer ? (
              <UserCombobox user={user} setUser={setUser} customer={customer} />
            ) : (
              <Button
                variant={"secondary"}
                className="h-fit min-h-[36px] w-72 justify-between gap-1 pl-2.5"
                disabled
              >
                <div className="text-sm font-normal text-gray-300">
                  Select User
                </div>
                <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            )}
          </div>
          {/* <div className="space-y-1">
            <InputHeading heading="Summary" />
            <textarea
              className="block w-[400px] resize-none rounded-md border-input p-2.5 py-2 text-sm shadow-sm outline-none transition-all placeholder:text-gray-300 focus:border-primary focus:outline-none focus:ring-[0.5px] focus:ring-primary"
              value={summary}
              onChange={(e) => {
                setSummary(e.target.value);
              }}
              ref={textAreaRef}
              rows={3}
              placeholder="What will this lesson cover?"
            />
          </div> */}
        </div>
        <div className="flex justify-end space-x-2 pt-4">
          <Button variant={"secondary"} onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            disabled={createNewEnrollmentMutation.isLoading}
            onClick={() => {
              if (!course || !customer || !user) return;
              createNewEnrollmentMutation.mutate({
                courseId: course?._id,
                customerId: customer._id.toString(),
                userId: user._id.toString(),
              });
              setOpen(false);
            }}
          >
            {createNewEnrollmentMutation.isLoading ? "Enrolling..." : "Enroll"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CourseCombobox({
  course,
  setCourse,
}: {
  course: ReducedCourseData | null;
  setCourse: (course: ReducedCourseData | null) => void;
}) {
  const { data: courses } = useGetCourses();
  const [open, setOpen] = useState(false);
  const [courseSearch, setCourseSearch] = useState("");
  return (
    <Popover modal={true} open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={"secondary"}
          className="h-fit min-h-[36px] w-72 justify-between gap-1 pl-2.5"
        >
          {!!course ? (
            <div className="truncate">{course.name}</div>
          ) : (
            <div className="text-sm font-normal text-gray-300">
              Select Course
            </div>
          )}
          <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="pointer-events-auto w-60 p-0" align="start">
        <Command className="relative">
          <CommandInput
            placeholder="Search user..."
            className="h-9"
            value={courseSearch}
            onValueChange={(s) => {
              setCourseSearch(s);
            }}
          />

          <CommandEmpty>No user found.</CommandEmpty>
          <CommandGroup>
            {courses?.map((course) => (
              <CommandItem
                key={course._id.toString()}
                value={course.name}
                onSelect={() => {
                  setCourse(course);
                  setOpen(false);
                }}
                className="gap-2"
              >
                <div className="flex items-center space-x-2">
                  <span className="truncate">{course.name}</span>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function UserCombobox({
  user,
  setUser,
  customer,
}: {
  user: CompanyUser | null;
  setUser: (user: CompanyUser | null) => void;
  customer: CustomerData;
}) {
  const { data: users } = useGetUsersForCompany(customer._id.toString());
  const [open, setOpen] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  return (
    <Popover modal={true} open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={"secondary"}
          className="h-fit min-h-[36px] w-72 justify-between gap-1 pl-2.5"
        >
          {!!user ? (
            <div className="truncate">{user.name}</div>
          ) : (
            <div className="text-sm font-normal text-gray-300">Select User</div>
          )}
          <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="pointer-events-auto w-60 p-0" align="start">
        <Command className="relative">
          <CommandInput
            placeholder="Search user..."
            className="h-9"
            value={userSearch}
            onValueChange={(s) => {
              setUserSearch(s);
            }}
          />

          <CommandEmpty>No user found.</CommandEmpty>
          <CommandGroup>
            {users?.map((user) => (
              <CommandItem
                key={user._id.toString()}
                value={user.name}
                onSelect={() => {
                  setUser(user);
                  setOpen(false);
                }}
                className="gap-2"
              >
                <div className="flex items-center space-x-2">
                  <span className="truncate">{user.name}</span>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function EnrollmentsTable({
  enrollments,
  search,
  setSearch,
}: {
  enrollments: CourseEnrollmentData[];
  search: string;
  setSearch: (search: string) => void;
}) {
  const { data: courses } = useGetCourses();
  const [sorting, setSorting] = useState<SortingState>([]);
  const columns: ColumnDef<CourseEnrollmentData>[] = useMemo(
    () => [
      {
        accessorKey: "user",
        header: "User",
        cell: ({ row }) => {
          return <div>{row.original.user.name}</div>;
        },
      },
      {
        accessorKey: "company",
        header: "Company",
        cell: ({ row }) => {
          return <div>{row.original.customer?.name}</div>;
        },
      },
      {
        accessorKey: "course",
        header: "Course",
        cell: ({ row }) => {
          return (
            <div>
              {courses?.find((c) => c._id === row.original.courseId)?.name}
            </div>
          );
        },
      },
      {
        accessorKey: "lessons",
        header: "Lessons",
        cell: ({ row }) => {
          return (
            <HoverCard openDelay={200} closeDelay={200}>
              <HoverCardTrigger asChild>
                <div className="flex items-center space-x-2">
                  {row.original.lessons.map((lesson) => (
                    <div
                      className={clsx(
                        "h-4 w-4 rounded-full border",
                        !lesson.isCompleted
                          ? lesson.timeViewed > 0
                            ? "border-blue-400 bg-blue-300"
                            : "border-gray-300 bg-gray-200"
                          : "border-green-400 bg-green-300"
                      )}
                      key={lesson.name}
                    />
                  ))}
                </div>
              </HoverCardTrigger>
              <HoverCardContent side="left" align="start">
                <div className="space-y-2.5">
                  {row.original.lessons.map((lesson) => {
                    const timeViewed = lesson.timeViewed;
                    const duration = lesson.totalTime;
                    const percentage = (timeViewed / duration) * 100;
                    return (
                      <div className="flex items-center space-x-6 text-sm">
                        <div className="w-40 truncate">{lesson.name}</div>
                        <div className="flex w-12 justify-end">
                          <div
                            className={clsx(
                              "w-fit rounded border p-0.5 px-1 text-xs font-medium",
                              lesson.isCompleted
                                ? "border-green-300 bg-green-100 text-green-600"
                                : percentage === 0
                                ? "border-gray-300 bg-gray-100 text-gray-600"
                                : "border-blue-300 bg-blue-100 text-blue-600"
                            )}
                          >
                            {percentage.toFixed(0)}%
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </HoverCardContent>
            </HoverCard>
          );
        },
      },
      {
        accessorKey: "enrollmentDate",
        header: "Enrollment Date",
        cell: ({ row }) => {
          return (
            <div>{dayjs(row.original.createdAt).format("MMM DD, YYYY")}</div>
          );
        },
      },
    ],
    []
  );
  const table = useReactTable<CourseEnrollmentData>({
    data: enrollments ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    state: {
      sorting,
      globalFilter: search,
    },
    globalFilterFn: fuzzyFilter,
    onSortingChange: setSorting,
    onGlobalFilterChange: setSearch,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });
  const navigate = useNavigate();
  return (
    <div className="flex-1 overflow-auto">
      <div className="max-h-full max-w-full overflow-auto">
        <table
          style={{ borderCollapse: "separate", borderSpacing: 0 }}
          className="w-full"
        >
          <thead className="overflow-auto">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="border-b">
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    scope="col"
                    className={
                      "sticky top-0 z-10 h-11 whitespace-nowrap border-b border-r border-gray-200/70 bg-gray-50 px-6 text-left text-xs font-normal uppercase tracking-wide text-gray-400 first:left-0 first:z-20 first:w-full first:pl-8"
                    }
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            <>
              {table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className="group relative rounded-lg hover:cursor-pointer hover:bg-gray-50"
                  // onClick={() => {
                  //   navigate(`/lms/lessons/${row.original._id}`);
                  // }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className={
                        "whitespace-nowrap border-r border-b border-gray-100 px-6 py-3 text-sm text-gray-500 first:sticky first:left-0 first:z-10 first:bg-white first:pr-6 first:pl-8 first:font-medium first:text-gray-900 first:group-hover:bg-gray-50"
                      }
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </>
          </tbody>
        </table>
      </div>
    </div>
  );
}
